import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../BaseController';
import { Inject } from '@odinjs/odin';
import { ActionAPI, FieldAPI, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { DevicesViewerAliases, FragmentNames } from '../../../codegen';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../constants/endpoints';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../constants/routes';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { PD_N2010 } from '../../../utils/parameters';
import moment from 'moment';
import { DevicesViewerFields as Type } from './devices-viewer-types';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import DevicesService from '../commons/devices-service';

@Controller({ component: DevicesViewerAliases.LAYOUT_DEVICES_VIEWER, fragment: FragmentNames.DEVICES_VIEWER })
class DevicesViewerController extends BaseController {

  @Inject(DevicesViewerAliases.FORM_DEVICES_VIEWER)
  private form!: FormAPI;

  @Inject(DevicesViewerAliases.ACTION_REMOVE)
  private actionRemoveAPI!: ActionAPI;

  @Inject(DevicesViewerAliases.ACTION_ANOTHER_ACTION)
  private anotherActionAPI!: ActionAPI;

  @Inject(DevicesViewerAliases.ACTION_REPLACE)
  private actionReplaceAPI!: ActionAPI;

  @Inject(DevicesViewerAliases.ACTION_EVENT)
  private actionEventAPI!: ActionAPI;

  @Inject(DevicesViewerAliases.FIELD_PROFESSIONAL)
  private fieldProfessionalAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_DEVICE_DESCRIPITION)
  private fieldDeviceDescAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_CALIBER)
  private fieldCaliberAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_DEVICE_COMPLEMENT)
  private fieldDeviceComplementAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_TOPOGRAPHY_DESCRIPTION)
  private fieldTopographyAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_SIDE)
  private fieldSideAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_DEVICES_USED_QUANTITY)
  private fieldDevicesQuantAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_MOTIVO_RETIRADA)
  private fieldMotivoRetiradaAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_PERMANENCIA)
  private fieldPermanenciaAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_ATTEMPT_TIMES)
  private fieldAttemptAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_INSTALLATION_DATE)
  private fieldInstallationAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_PREVIEW_END_DATE)
  private fieldPreviewEndDateAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_END_DATE)
  private fieldEndDataAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_BANDAGE_HOURS_PERMANENCE)
  private fieldBandageHoursAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_BANDAGE_SCHEDULE_REMOVAL)
  private fieldBandageScheduleAPI!: FieldAPI;

  @Inject(DevicesViewerAliases.FIELD_NOTE_DESCRIPTION)
  private fieldNoteAPI!: FieldAPI;

  private validateVisibleFields(record: Record<string, unknown>): void {
    const keys = Object.keys(record);
    keys.forEach(key => {
      switch (key) {
        case Type.DEVICE_DESCRIPTION:
          this.fieldDeviceDescAPI.setVisible(true);
          break;
        case Type.CALIBER:
          this.fieldCaliberAPI.setVisible(true);
          break;
        case Type.DEVICE_COMPLEMENT:
          this.fieldDeviceComplementAPI.setVisible(true);
          break;
        case Type.TOPOGRAPHY_DESCRIPTION:
          this.fieldTopographyAPI.setVisible(true);
          break;
        case Type.SIDE:
          this.fieldSideAPI.setVisible(true);
          break;
        case Type.DEVICES_USED_QUANTITY:
          this.fieldDevicesQuantAPI.setVisible(true);
          break;
        case Type.HOURS_OF_PERMANENCE:
          this.fieldPermanenciaAPI.setVisible(true);
          break;
        case Type.TIMES_ATTEMPTED:
          this.fieldAttemptAPI.setVisible(true);
          break;
        case Type.INSTALLATION_DATE:
          this.fieldInstallationAPI.setVisible(true);
          break;
        case Type.SCHEDULED_END_DATE:
          this.fieldPreviewEndDateAPI.setVisible(true);
          break;
        case Type.END_DATE:
          this.fieldEndDataAPI.setVisible(true);
          break;
        case Type.BANDAGE_SCHEDULE_REMOVAL:
          this.fieldBandageScheduleAPI.setVisible(true);
          break;
        case Type.NOTES_DESCRIPTION:
          this.fieldNoteAPI.setVisible(true);
          break;
        case Type.PROFESSIONAL_NAME:
          this.fieldProfessionalAPI.setVisible(true);
          break;
        case Type.BANDAGE_HOURS_OF_PERMANENCE:
          this.fieldBandageHoursAPI.setVisible(true);
          break;
        case Type.RETIRE_REASON:
          this.fieldMotivoRetiradaAPI.setVisible(true);
          break;
        default:
          break;
      }
    })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private enablingActions(): void {
    const allowInfo = InformationBarUtils.allowInfo && DevicesService.isAllowEdit;

    if (!allowInfo) {
      this.actionEventAPI.setEnabled(false);
      this.actionRemoveAPI.setEnabled(false);
      this.actionReplaceAPI.setEnabled(false);
      this.anotherActionAPI.setEnabled(false);
      return;
    }

    this.validateActions();
  }

  @OnMount(DevicesViewerAliases.LAYOUT_DEVICES_VIEWER)
  onMount(): void {

    this.enablingActions();
    InformationBarUtils.collapsedBar = true;
    const { patientEncounterDeviceId, patientId, encounterId, deviceSequence } = this.historyService.getCurrentRouteParams();
    this.context.set({
      deviceSequence,
      encounterId: encounterId,
      patientEncounterDeviceId: patientEncounterDeviceId,
      patientId: patientId
    });
    this.form.setLoading(true);
    HttpService.get(Endpoint.GET_DEVICES_VIEWER, {
      encounterId,
      patientEncounterDeviceId,
      patientId
    }).then((response) => {
      const res = response.body as Record<string, unknown>;
      const finalRes = {
        ...res,
        bandageScheduledRemoval: res.bandageScheduledRemoval && moment(res.bandageScheduledRemoval as string).format('L LTS'),
        endDate: res.endDate && moment(res.endDate as string).format('L LTS'),
        installationDate: res.installationDate && moment(res.installationDate as string).format('L LTS'),
        scheduledEndDate: res.scheduledEndDate && moment(res.scheduledEndDate as string).format('L LTS'),
      }
      const record = { ...this.form.getData(), ...finalRes } as Record<string, Value>;
      this.validateVisibleFields(res);
      this.form.setData(record);
    }).catch((err) => {
      this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
    }).finally(() => this.form.setLoading(false));
  }

  validateActions(): void {
    const concatActions = String(FoundationUtils.findModuleParam('PATIENT_DEVICE#-2010', PD_N2010.DEVICE_ACTIONS_ALLOWED));
    const actionsMap: { [key: string]: () => void } = {
      E: () => this.actionEventAPI.setEnabled(true),
      O: () => this.anotherActionAPI.setEnabled(true),
      R: () => this.actionRemoveAPI.setEnabled(true),
      S: () => this.actionReplaceAPI.setEnabled(true),
    };

    if (!concatActions || concatActions === 'null') {
      Object.values(actionsMap).forEach((enableAction) => enableAction());
    } else {
      for (const action of concatActions) {
        const enableAction = actionsMap[action];
        if (enableAction) enableAction();
      }
    }
  }

  @OnClick(DevicesViewerAliases.ACTION_REPLACE)
  onClickReplace(): void {
    const { patientEncounterDeviceId, patientId, encounterId, deviceSequence } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.DEVICES_REPLACE_ACTION, {
      deviceSequence,
      encounterId,
      patientEncounterDeviceId,
      patientId
    });
  }

  @OnClick(DevicesViewerAliases.ACTION_ANOTHER_ACTION)
  onClickAnotherAction(): void {
    const { patientEncounterDeviceId, patientId, encounterId, deviceSequence } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.DEVICES_ANOTHER_ACTION, {
      deviceSequence,
      encounterId,
      patientEncounterDeviceId,
      patientId
    });
  }

  @OnClick(DevicesViewerAliases.ACTION_REMOVE)
  handleClickRemoveAction(): void {
    const { patientEncounterDeviceId, patientId, encounterId, deviceSequence } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.DEVICES_REMOVE_ACTION, {
      deviceSequence,
      encounterId,
      patientEncounterDeviceId,
      patientId
    });
  }

  @OnClick(DevicesViewerAliases.ACTION_EVENT)
  onClickEventAction(): void {
    const { patientEncounterDeviceId, patientId, encounterId, deviceSequence } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.DEVICES_EVENTS_LIST, {
      deviceSequence,
      encounterId,
      patientEncounterDeviceId,
      patientId
    });
  }

}

export default DevicesViewerController;
